import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DraggerManager } from "../draggers/DraggerManager";
import type { InteractiveLayer } from "../overlay/InteractiveLayer";
import type { SnapManager, ObjectSnapMode } from "../snap";
import { DraggerType } from "../draggers/types";
import { SNAP_GUIDE_STYLE } from "../draggers/previewStyles";
import type { OverlayStyle, PolylineOverlay } from "../overlay/types";
import { OverlayType } from "../overlay/types";
import { GestureRecognizer } from "./GestureRecognizer";
import { NavigationHandler } from "./NavigationHandler";
import {
  GestureType,
  PointerType,
  InteractionState,
  InputMode,
  type GestureEvent,
  type GestureConfig,
  type Point2D,
  type Point3D,
} from "./types";

/**
 * Event handler type for interaction events.
 */
type InteractionEventHandler = (state: InteractionState) => void;

/**
 * InteractionManager - Routes pointer events based on current state and input mode.
 *
 * This is the SINGLE event router for the drawing system:
 * - GestureRecognizer detects gestures and sends them here
 * - InteractionManager routes to either NavigationHandler or active Dragger
 * - Draggers do NOT bind their own events
 *
 * Responsibilities:
 * - Coordinate between navigation (pan/zoom) and drawing tools
 * - Handle stylus mode (pen draws, finger navigates)
 * - Manage interaction state machine
 * - Route gestures to appropriate handlers
 */
export class InteractionManager {
  private viewer: DRDrawingViewer;
  private draggerManager: DraggerManager;
  private gestureRecognizer: GestureRecognizer;
  private _navigationHandler: NavigationHandler;

  private _state = InteractionState.Idle;
  private _inputMode = InputMode.Normal;
  private inputModeHandlers: Array<(mode: InputMode) => void> = [];

  // Pan key state (Enter key for laptops without middle mouse button)
  private isPanKeyHeld = false;

  // Event handlers
  private stateChangeHandlers: InteractionEventHandler[] = [];

  // Interactive overlay layer (optional)
  private interactiveLayer: InteractiveLayer | null = null;

  // Snap manager for ortho/polar/snap processing
  private snapManager: SnapManager | null = null;

  // Last confirmed drawing point (reference for ortho constraint)
  private lastDrawingPoint: Point3D | null = null;

  // Hover snap throttling (~16 fps instead of ~60 fps)
  private _lastHoverSnapTime = 0;
  private static readonly HOVER_SNAP_INTERVAL = 60; // ms

  // Snap marker overlay IDs (for visual feedback - array for multi-part markers like X)
  private snapMarkerIds: string[] = [];

  // Constraint guide line overlay ID (dashed line for ortho/polar feedback)
  private constraintGuideId: string | null = null;

  constructor(
    viewer: DRDrawingViewer,
    draggerManager: DraggerManager,
    gestureConfig?: Partial<GestureConfig>,
  ) {
    this.viewer = viewer;
    this.draggerManager = draggerManager;
    this.gestureRecognizer = new GestureRecognizer(
      viewer.canvas,
      gestureConfig,
    );
    this._navigationHandler = new NavigationHandler(viewer);
  }

  // ============== Interactive Layer ==============

  /**
   * Set the interactive layer for overlay hit testing and drag handling.
   */
  setInteractiveLayer(layer: InteractiveLayer): void {
    this.interactiveLayer = layer;
  }

  /**
   * Set the snap manager for ortho/polar/snap processing.
   */
  setSnapManager(manager: SnapManager): void {
    this.snapManager = manager;
  }

  /**
   * Get the navigation handler (for attaching InteractiveLayer).
   */
  get navigationHandler(): NavigationHandler {
    return this._navigationHandler;
  }

  // ============== Lifecycle ==============

  /**
   * Start handling interactions.
   */
  attach(): void {
    this.gestureRecognizer.attach({
      onStart: this.handleGestureStart,
      onMove: this.handleGestureMove,
      onEnd: this.handleGestureEnd,
      onLeave: this.handleGestureLeave,
      onHover: this.handleHover,
    });

    // Keyboard handlers for pan key (Enter)
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
  }

  /**
   * Stop handling interactions.
   */
  detach(): void {
    this.gestureRecognizer.detach();
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    this.isPanKeyHeld = false;
  }

  /**
   * Dispose the interaction manager.
   */
  dispose(): void {
    this.detach();
    this._navigationHandler.dispose();
    this.stateChangeHandlers = [];
  }

  // ============== State ==============

  /**
   * Get the current interaction state.
   */
  get state(): InteractionState {
    return this._state;
  }

  /**
   * Set the interaction state.
   */
  private setState(newState: InteractionState): void {
    if (this._state === newState) return;

    this._state = newState;

    // Notify handlers
    for (const handler of this.stateChangeHandlers) {
      handler(newState);
    }
  }

  /**
   * Get the current input mode.
   */
  get inputMode(): InputMode {
    return this._inputMode;
  }

  /**
   * Set the input mode (normal vs stylus-only).
   */
  set inputMode(mode: InputMode) {
    this.applyInputMode(mode);
  }

  /**
   * Set the input mode (normal vs stylus-only).
   * @deprecated Use inputMode setter instead
   */
  setInputMode(mode: InputMode): void {
    this.applyInputMode(mode);
  }

  /**
   * Subscribe to input mode changes (e.g. the rulers, grabbed by the finger
   * in stylus mode).
   * @returns Unsubscribe function
   */
  onInputModeChange(handler: (mode: InputMode) => void): () => void {
    this.inputModeHandlers.push(handler);
    return () => {
      const index = this.inputModeHandlers.indexOf(handler);
      if (index >= 0) this.inputModeHandlers.splice(index, 1);
    };
  }

  private applyInputMode(mode: InputMode): void {
    if (mode === this._inputMode) return;
    this._inputMode = mode;
    for (const handler of this.inputModeHandlers) handler(mode);
  }

  /**
   * Subscribe to state changes.
   */
  onStateChange(handler: InteractionEventHandler): () => void {
    this.stateChangeHandlers.push(handler);
    return () => {
      const index = this.stateChangeHandlers.indexOf(handler);
      if (index >= 0) {
        this.stateChangeHandlers.splice(index, 1);
      }
    };
  }

  // ============== Keyboard Handlers ==============

  private handleKeyDown = (ev: KeyboardEvent): void => {
    const target = ev.target as HTMLElement;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target.isContentEditable
    ) {
      return;
    }
    if ((ev.key === "Enter" || ev.key === " ") && !ev.repeat) {
      this.isPanKeyHeld = true;
      if (ev.key === " ") {
        ev.preventDefault(); // prevent page scroll
      }
    }
  };

  private handleKeyUp = (ev: KeyboardEvent): void => {
    if (ev.key === "Enter" || ev.key === " ") {
      this.isPanKeyHeld = false;
    }
  };

  // ============== Gesture Routing ==============

  private handleGestureStart = (event: GestureEvent): void => {
    const route = this.determineRoute(event);

    switch (route) {
      case "draw":
        this.setState(InteractionState.Drawing);
        this.routeToDragger("start", event);
        break;

      case "navigate":
        this.setState(InteractionState.Navigating);
        this._navigationHandler.onNavigationStart(event);
        break;

      case "overlay":
        this.setState(InteractionState.Transforming);
        this.startOverlayDrag(event);
        break;

      case "select":
        this.setState(InteractionState.Selecting);
        // TODO: Route to selection handler
        break;

      case "none":
        // Let event propagate naturally
        break;
    }
  };

  private handleGestureMove = (event: GestureEvent): void => {
    switch (this._state) {
      case InteractionState.Drawing:
        this.routeToDragger("move", event);
        break;

      case InteractionState.Navigating:
        this._navigationHandler.onNavigationMove(event);
        break;

      case InteractionState.Selecting:
        // TODO: Route to selection handler
        break;

      case InteractionState.Transforming:
        // Route to overlay drag handler
        if (this.interactiveLayer?.isDragging) {
          this.moveOverlayDrag(event);
        }
        break;

      case InteractionState.Idle:
        // Do nothing on move while idle - wait for proper start event
        // This prevents accidental drawing from hover events
        break;
    }
  };

  private handleGestureEnd = (event: GestureEvent): void => {
    this.finishGesture(event, "end");
  };

  private handleGestureLeave = (event: GestureEvent): void => {
    this.finishGesture(event, "leave");
  };

  /**
   * Shared tail for both a normal release (handleGestureEnd) and the
   * pointer leaving the canvas mid-drag (handleGestureLeave). Only the
   * dragger-routing phase differs between the two — Navigating/Transforming
   * end-of-gesture handling and the return to Idle stay identical either way.
   */
  private finishGesture(event: GestureEvent, phase: "end" | "leave"): void {
    // First, end any active interaction
    switch (this._state) {
      case InteractionState.Drawing:
        this.routeToDragger(phase, event);
        break;

      case InteractionState.Navigating:
        this._navigationHandler.onNavigationEnd(event);
        break;

      case InteractionState.Transforming:
        if (this.interactiveLayer?.isDragging) {
          this.endOverlayDrag(event);
        }
        break;
    }

    // Then handle special gesture types
    switch (event.type) {
      case GestureType.Tap:
        this.handleTap(event);
        break;

      case GestureType.DoubleTap:
        this.handleDoubleTap(event);
        break;

      case GestureType.LongPress:
        this.handleLongPress(event);
        break;
    }

    // Return to idle
    this.setState(InteractionState.Idle);
  }

  // ============== Hover Snap ==============

  /**
   * Handle hover events (mouse move without button pressed).
   * Shows snap markers when the cursor is near snap points. When a dragger
   * has a pending placement (e.g. point A already fixed in two-point mode,
   * or a polyline's last committed vertex), also applies full ortho/polar
   * snap processing and forwards the point to the dragger so its preview
   * (rubber band) follows the cursor between clicks — AutoCAD-style.
   */
  private handleHover = (screenPosition: Point2D): void => {
    // Only process hover snap when idle and snap is enabled
    if (this._state !== InteractionState.Idle || !this.snapManager) {
      return;
    }

    // Throttle hover snap to ~16 fps — snap marker feedback tolerates 50-100ms latency
    const now = performance.now();
    if (now - this._lastHoverSnapTime < InteractionManager.HOVER_SNAP_INTERVAL)
      return;
    this._lastHoverSnapTime = now;

    const dragger = this.draggerManager.current;
    const snapReference = dragger?.getSnapReferencePoint() ?? undefined;

    let screen: Point2D = screenPosition;
    let world: Point3D = this.viewer.screenToWorld(screen);

    const result = this.snapManager.processPoint(
      world,
      snapReference,
      screen,
      snapReference
        ? dragger?.getSnapOptions()
        : {
            acceptObject: true,
            acceptOrtho: false,
            acceptPolar: false,
          },
    );
    world = result.point;

    if (result.constraintType === "osnap" && result.osnapMode) {
      this.showSnapMarker(world, result.osnapMode);
    } else {
      this.hideSnapMarker();
    }

    // Show/hide constraint guide line for ortho/polar — only meaningful once
    // a placement is pending (nothing to draw the guide from otherwise).
    if (
      snapReference &&
      (result.constraintType === "ortho" || result.constraintType === "polar")
    ) {
      this.showConstraintGuide(snapReference, world);
    } else {
      this.hideConstraintGuide();
    }

    if (result.constrained) {
      screen = this.viewer.worldToScreen(world);
    }

    if (dragger && snapReference) {
      dragger.handleHoverMove(screen, world);
    }
  };

  // ============== Dragger Routing ==============

  /**
   * Route gesture event to the active dragger.
   * Applies snap/ortho processing before passing to dragger.
   */
  private routeToDragger(
    phase: "start" | "move" | "end" | "leave",
    event: GestureEvent,
  ): void {
    const dragger = this.draggerManager.current;
    if (!dragger) return;

    // Convert screen to world coordinates
    let screen: Point2D = event.screenPosition;
    let world: Point3D = this.viewer.screenToWorld(screen);

    // Use the dragger's snap reference point (e.g. last committed vertex for polylines)
    // falling back to the InteractionManager's lastDrawingPoint
    const snapReference =
      dragger.getSnapReferencePoint() ?? this.lastDrawingPoint ?? undefined;

    // Apply snap/constraint processing (ortho, polar, object snap)
    if (this.snapManager) {
      const snapOptions = dragger.getSnapOptions();
      const result = this.snapManager.processPoint(
        world,
        snapReference,
        screen, // Pass screen for object snap aperture check
        snapOptions,
      );
      world = result.point;
      // Show/hide snap marker based on result
      if (result.constraintType === "osnap" && result.osnapMode) {
        this.showSnapMarker(world, result.osnapMode);
      } else {
        this.hideSnapMarker();
      }

      // Show/hide constraint guide line for ortho/polar
      if (
        (result.constraintType === "ortho" ||
          result.constraintType === "polar") &&
        snapReference
      ) {
        this.showConstraintGuide(snapReference, world);
      } else {
        this.hideConstraintGuide();
      }

      // Convert constrained world back to screen for accurate preview
      if (result.constrained) {
        screen = this.viewer.worldToScreen(world);
      }
    }

    switch (phase) {
      case "start":
        // Only set lastDrawingPoint if the dragger doesn't provide its own reference.
        // For sequential draggers (polylines), the reference is the last committed vertex,
        // not the position where the user starts a new gesture. And not at all
        // for a point placed on release (two-point mode, first polyline vertex,
        // base point): ortho would otherwise pull that point onto the
        // horizontal/vertical of where the drag began.
        if (!dragger.getSnapReferencePoint() && dragger.snapsToGestureStart()) {
          this.lastDrawingPoint = world;
        }
        dragger.handleStart(screen, world, event.originalEvent);
        break;
      case "move":
        dragger.handleMove(screen, world, event.originalEvent);
        break;
      case "end":
        this.lastDrawingPoint = null;
        // Hide snap marker and constraint guide on end
        this.hideSnapMarker();
        this.hideConstraintGuide();
        dragger.handleEnd(screen, world, event.originalEvent);
        break;
      case "leave":
        this.lastDrawingPoint = null;
        this.hideSnapMarker();
        this.hideConstraintGuide();
        dragger.handleLeave(screen, world, event.originalEvent);
        break;
    }
  }

  // ============== Snap Marker ==============

  /**
   * Show a visual marker at the snap point.
   * Different shapes indicate different snap modes.
   */
  private showSnapMarker(worldPoint: Point3D, mode: ObjectSnapMode): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const screen = this.viewer.worldToScreen(worldPoint);
    const size = 8;
    const style: Partial<OverlayStyle> = {
      strokeColor: "#00ff00",
      strokeWidth: 2,
      fillColor: "transparent",
    };

    // Remove existing marker
    this.hideSnapMarker();

    switch (mode) {
      case "endpoint":
        // Square marker
        this.snapMarkerIds.push(
          overlay.createRectangle(
            screen.x - size / 2,
            screen.y - size / 2,
            size,
            size,
            style,
          ),
        );
        break;

      case "midpoint":
        // Triangle marker (pointing up)
        this.snapMarkerIds.push(
          overlay.add({
            type: OverlayType.Polyline,
            points: [
              { x: screen.x, y: screen.y - size },
              { x: screen.x - size, y: screen.y + size / 2 },
              { x: screen.x + size, y: screen.y + size / 2 },
            ],
            closed: true,
            style: {
              ...style,
              strokeColor: "#00ff00",
              fillColor: "transparent",
            },
          } as Omit<PolylineOverlay, "id">),
        );
        break;

      case "center":
        // Circle marker
        this.snapMarkerIds.push(
          overlay.createCircle(screen.x, screen.y, size, style),
        );
        break;

      case "quadrant":
        // Diamond marker (rotated square)
        this.snapMarkerIds.push(
          overlay.add({
            type: OverlayType.Polyline,
            points: [
              { x: screen.x, y: screen.y - size },
              { x: screen.x + size, y: screen.y },
              { x: screen.x, y: screen.y + size },
              { x: screen.x - size, y: screen.y },
            ],
            closed: true,
            style: {
              ...style,
              strokeColor: "#00ff00",
              fillColor: "transparent",
            },
          } as Omit<PolylineOverlay, "id">),
        );
        break;

      case "nearest":
        // X marker (two lines)
        this.snapMarkerIds.push(
          overlay.add({
            type: OverlayType.Polyline,
            points: [
              { x: screen.x - size, y: screen.y - size },
              { x: screen.x + size, y: screen.y + size },
            ],
            closed: false,
            style: { ...style, strokeColor: "#00ff00" },
          } as Omit<PolylineOverlay, "id">),
        );
        // Second line for X
        this.snapMarkerIds.push(
          overlay.add({
            type: OverlayType.Polyline,
            points: [
              { x: screen.x + size, y: screen.y - size },
              { x: screen.x - size, y: screen.y + size },
            ],
            closed: false,
            style: { ...style, strokeColor: "#00ff00" },
          } as Omit<PolylineOverlay, "id">),
        );
        break;

      default:
        // Generic marker for modes without a dedicated glyph
        this.snapMarkerIds.push(
          overlay.createCircle(screen.x, screen.y, size * 0.6, style),
        );
        break;
    }
  }

  /**
   * Hide the snap marker.
   */
  private hideSnapMarker(): void {
    if (this.snapMarkerIds.length > 0 && this.viewer.overlayManager) {
      for (const id of this.snapMarkerIds) {
        this.viewer.overlayManager.remove(id);
      }
      this.snapMarkerIds = [];
    }
  }

  // ============== Constraint Guide Line ==============

  /**
   * Show a dashed guide line along the constraint axis/angle.
   * The line extends from lastPoint through the snapped point,
   * well beyond the visible area in both directions.
   */
  private showConstraintGuide(lastPoint: Point3D, snappedPoint: Point3D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const lastScreen = this.viewer.worldToScreen(lastPoint);
    const snappedScreen = this.viewer.worldToScreen(snappedPoint);

    // Compute direction vector in screen space
    const dx = snappedScreen.x - lastScreen.x;
    const dy = snappedScreen.y - lastScreen.y;
    const len = Math.sqrt(dx * dx + dy * dy);

    // If points are coincident, nothing to draw
    if (len < 1e-6) return;

    const dirX = dx / len;
    const dirY = dy / len;

    // Extend line well beyond the viewport in both directions from lastPoint
    const bigDistance = 5000;
    const x1 = lastScreen.x - dirX * bigDistance;
    const y1 = lastScreen.y - dirY * bigDistance;
    const x2 = lastScreen.x + dirX * bigDistance;
    const y2 = lastScreen.y + dirY * bigDistance;

    if (this.constraintGuideId) {
      // Update existing guide line
      overlay.update(this.constraintGuideId, { x1, y1, x2, y2 });
    } else {
      // Create new guide line
      this.constraintGuideId = overlay.createLine(
        x1,
        y1,
        x2,
        y2,
        SNAP_GUIDE_STYLE,
      );
    }
  }

  /**
   * Hide the constraint guide line.
   */
  private hideConstraintGuide(): void {
    if (this.constraintGuideId && this.viewer.overlayManager) {
      this.viewer.overlayManager.remove(this.constraintGuideId);
      this.constraintGuideId = null;
    }
  }

  // ============== Overlay Drag Routing ==============

  /**
   * Start dragging an overlay element.
   * InteractiveLayer handles calling the overlay's onDragStart handler.
   */
  private startOverlayDrag(event: GestureEvent): void {
    if (!this.interactiveLayer) return;

    const hit = this.interactiveLayer.hitTest(
      event.screenPosition,
      event.pointerType,
    );
    if (hit) {
      this.interactiveLayer.handleDragStart(hit);
    }
  }

  /**
   * Update overlay drag position.
   * InteractiveLayer handles calling the overlay's onDragMove handler.
   * Applies snap/constraint processing for vertex handle movement.
   */
  private moveOverlayDrag(event: GestureEvent): void {
    if (
      this.interactiveLayer?.isDragging &&
      this.interactiveLayer?.draggedOverlay
    ) {
      let screen = event.screenPosition;
      let world = this.viewer.screenToWorld(screen);

      // Apply snap/constraint processing (ortho, polar, object snap)
      if (this.snapManager && this.interactiveLayer?.draggedOverlay) {
        /** use handle specific options */
        const overlay = this.interactiveLayer.draggedOverlay;

        const snapOptions = overlay.snapOptions;

        const result = this.snapManager.processPoint(
          world,
          this.lastDrawingPoint ?? undefined,
          screen,
          snapOptions,
        );
        world = result.point;

        // Show/hide snap marker based on result
        if (result.constraintType === "osnap" && result.osnapMode) {
          this.showSnapMarker(world, result.osnapMode);
        } else {
          this.hideSnapMarker();
        }

        // Show/hide constraint guide line for ortho/polar
        if (
          (result.constraintType === "ortho" ||
            result.constraintType === "polar") &&
          this.lastDrawingPoint
        ) {
          this.showConstraintGuide(this.lastDrawingPoint, world);
        } else {
          this.hideConstraintGuide();
        }

        // Convert constrained world back to screen for accurate display
        if (result.constrained) {
          screen = this.viewer.worldToScreen(world);
        }
      }

      this.interactiveLayer.handleDragMove(screen, world);
    }
  }

  /**
   * End overlay drag.
   * InteractiveLayer handles calling the overlay's onDragEnd handler.
   * Applies snap/constraint processing for final position.
   */
  private endOverlayDrag(event: GestureEvent): void {
    if (!this.interactiveLayer?.isDragging) return;

    let screen = event.screenPosition;
    let world = this.viewer.screenToWorld(screen);

    // Apply snap/constraint processing for final position
    if (this.snapManager) {
      const snapOptions = this.interactiveLayer.draggedOverlay?.snapOptions;
      const result = this.snapManager.processPoint(
        world,
        this.lastDrawingPoint ?? undefined,
        screen,
        snapOptions,
      );
      world = result.point;

      // Convert constrained world back to screen
      if (result.constrained) {
        screen = this.viewer.worldToScreen(world);
      }
    }

    // Hide snap marker and constraint guide on end
    this.hideSnapMarker();
    this.hideConstraintGuide();

    this.interactiveLayer.handleDragEnd(screen, world);
  }

  // ============== Route Determination ==============

  /**
   * Determine how to route a gesture based on current state and input mode.
   */
  private determineRoute(
    event: GestureEvent,
  ): "draw" | "navigate" | "select" | "overlay" | "none" {
    const activeTool = this.draggerManager.currentType;
    const hasActiveTool = activeTool !== DraggerType.None;
    const isTouch = event.pointerType === PointerType.Touch;
    const isPen = event.pointerType === PointerType.Pen;
    const isTwoFinger =
      event.type === GestureType.Pinch ||
      event.type === GestureType.TwoFingerPan;
    const isMiddleButton = event.button === 1;
    const isRightButton = event.button === 2;
    const isLeftButton = event.button === 0 || event.button === undefined; // undefined for touch/pen

    // Middle mouse button always navigates (pan)
    if (isMiddleButton) {
      return "navigate";
    }

    // Right mouse button - ignore for now (could be context menu)
    if (isRightButton) {
      return "none";
    }

    // Pan key (Enter) held - navigate (for laptops without middle mouse button)
    if (this.isPanKeyHeld) {
      return "navigate";
    }

    // Two-finger gestures always navigate
    if (isTwoFinger) {
      return "navigate";
    }

    // Check for overlay hit (rulers, handles) before other routing
    // Overlay interaction takes priority when clicking on interactive elements
    if (this.interactiveLayer) {
      const hit = this.interactiveLayer.hitTest(
        event.screenPosition,
        event.pointerType,
      );
      if (hit) {
        return "overlay";
      }
    }

    // Only left button, touch, or pen can draw
    if (!isLeftButton && !isTouch && !isPen) {
      return "navigate";
    }

    // Stylus mode: pen/mouse draws, touch navigates
    if (this._inputMode === InputMode.StylusOnly) {
      if (isTouch) {
        return "navigate";
      }
      // Pen and mouse both draw (mouse is also a precise input device)
      if (hasActiveTool) {
        return "draw";
      }
      return "navigate";
    }

    // Normal mode: draws if tool active, otherwise navigates
    if (this._inputMode === InputMode.Normal) {
      if (hasActiveTool) {
        return "draw";
      }
      // No tool active - let navigation happen
      return "navigate";
    }

    return "none";
  }

  // ============== Special Gesture Handlers ==============

  private handleTap(event: GestureEvent): void {
    // Tap behavior depends on current tool
    const activeTool = this.draggerManager.currentType;

    // Only allow tap with left button (0), touch, or pen
    const isLeftButton = event.button === 0 || event.button === undefined;
    const isTouch = event.pointerType === PointerType.Touch;
    const isPen = event.pointerType === PointerType.Pen;

    if (!isLeftButton && !isTouch && !isPen) {
      return;
    }

    // Check overlay hit first (consistent with determineRoute priority)
    if (this.interactiveLayer) {
      const hit = this.interactiveLayer.hitTest(
        event.screenPosition,
        event.pointerType,
      );
      if (hit) {
        this.interactiveLayer.handleDragStart(hit);
        const screen = event.screenPosition;
        const world = this.viewer.screenToWorld(screen);
        this.interactiveLayer.handleDragEnd(screen, world);
        return;
      }
    }

    if (activeTool === DraggerType.None) {
      // No tool - could be selection tap (future)
    } else {
      // Tool active - route tap as a quick start/end
      this.routeToDragger("start", event);
      this.routeToDragger("end", event);
    }
  }

  private handleDoubleTap(_event: GestureEvent): void {
    // Double tap - zoom extents
  }

  private handleLongPress(_event: GestureEvent): void {
    // Long press - could show context menu (future)
  }

  // ============== Public API ==============

  /**
   * Check if navigation is currently active.
   */
  get isNavigating(): boolean {
    return this._state === InteractionState.Navigating;
  }

  /**
   * Check if drawing is currently active.
   */
  get isDrawing(): boolean {
    return this._state === InteractionState.Drawing;
  }

  /**
   * Check if the system is idle.
   */
  get isIdle(): boolean {
    return this._state === InteractionState.Idle;
  }

  /**
   * Force return to idle state.
   */
  forceIdle(): void {
    this.setState(InteractionState.Idle);
  }
}
